/**
 * @fileoverview AR-924 — Close guard of the teacher deliverable drawer: confirms (Mantine modal) before
 * leaving with an upload in flight or an unsaved date edit, running `onLeave` first.
 */

import { useCallback } from 'react'
import { Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useTranslation } from 'react-i18next'

export type LeaveReason = 'upload_in_progress' | 'unsaved_date'

interface UseLeaveGuardArgs {
  readonly reason: LeaveReason | null
  readonly onLeave?: () => void
  readonly onClose: () => void
}

export function useLeaveGuard({ reason, onLeave, onClose }: UseLeaveGuardArgs): () => void {
  const { t } = useTranslation()

  return useCallback(() => {
    if (!reason) {
      onClose()
      return
    }
    const tr = (key: string) => t(`task_tracker.teacher_upload.${key}`)
    modals.openConfirmModal({
      title: tr('leave_title'),
      centered: true,
      children: <Text size="sm">{tr(`leave_${reason}`)}</Text>,
      labels: { confirm: tr('leave_confirm'), cancel: tr('leave_cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        onLeave?.()
        onClose()
      },
    })
  }, [reason, onLeave, onClose, t])
}
